import { BrokerState } from './types/BrokerState';
import { CustoDropDownItemProps } from './components/types';
import { EstateState } from './types/EstateState';
import { InsurerState } from './types/InsurerState';
import { FranchisesBasicState, MultiFranchisesBasicState } from './types/FranchiseState';
import { RolesBasicState, TranslatedBrokerRolesEnum } from './types/Roles';
import moment from 'moment-timezone';
import { CustomDropDownOptions } from './types';
import { UserRolesEnum } from './enums/user-roles.enum';
import { UserStatusEnum } from './enums/user-status.enum';
import { useEffect, useState } from 'react';
import { menuOptions } from './services/menu';

export const EMPTY_DATE = new Date('2000-01-01');
export const IOF = 0.0738;

export const isBackofficeTestUser = (user?: {
  role?: string;
  isBackofficeTest?: boolean;
  isReadOnly?: boolean;
}): boolean =>
  Boolean(
    user?.isBackofficeTest ||
      user?.isReadOnly ||
      user?.role === UserRolesEnum.backofficeTest
  );

export function PushRoute(history: any, reduxProps: any, route: string, params?: any) {
  const selectedMenu = menuOptions
    .flatMap(item => [item, ...(item.submenuItems || [])])
    .find(item => item.route === route);

  if (selectedMenu) {
    if (reduxProps.currentPage) {
      reduxProps.updateCurrentPage(selectedMenu);
    }

    history.push('./' + route, params);
  }
}

export const allDataAreFilled = (data: any, exceptions?: Array<string>) => {
  if (!data || !Object.entries(data).length) {
    return false;
  }

  const dataCopy = { ...data };

  if (exceptions) {
    exceptions.forEach(exception => {
      if (exception in dataCopy) {
        delete dataCopy[exception];
      }
    });
  }
  return Object.keys(dataCopy).every(key => {
    const value = dataCopy[key];

    return value !== null && value !== '' && value !== EMPTY_DATE && value !== '0,00';
  });
};

export const BrokersToCustomDropDownOptions = (
  completeValue: boolean,
  brokers: BrokerState[]
) => {
  return brokers.map((broker: BrokerState, index: number): CustoDropDownItemProps => {
    return {
      value: completeValue ? { ...broker } : broker.id,
      key: index,
      text: broker.name
    };
  });
};

export const EstatesToCustomDropDownOptions = (
  completeValue: boolean,
  estates: EstateState[]
) => {
  return estates.map((estate: EstateState, index: number): CustoDropDownItemProps => {
    return {
      value: completeValue ? { ...estate } : estate.id,
      key: index,
      text: estate.name
    };
  });
};

export const InsurersToCustomDropDownOptions = (
  completeValue: boolean,
  insurers: InsurerState[]
) => {
  return insurers.map((insurer: InsurerState, index: number): CustoDropDownItemProps => {
    return {
      value: completeValue ? { ...insurer } : insurer.id,
      key: index,
      text: insurer.name
    };
  });
};

export const RolesToCustomDropDownOptions = (roles: RolesBasicState[]) => {
  const brokerRoles = roles.filter(
    role =>
      role.name !== UserRolesEnum.estateAdmin &&
      role.name !== UserRolesEnum.estateUser &&
      role.name !== UserRolesEnum.estateQuotation
  );
  return brokerRoles.map(
    (role: RolesBasicState, index: number): CustoDropDownItemProps => {
      return {
        value: role.name,
        key: index,
        text: translateUserRole(role.name)
      };
    }
  );
};

export const FranchisesToCustomDropDownOptions = (franchises: FranchisesBasicState[]) => {
  return franchises?.map(
    (franchise: FranchisesBasicState, index: number): CustoDropDownItemProps => {
      return {
        value: franchise.id,
        key: index,
        text: franchise.name
      };
    }
  );
};

export const MultFranchisesToCustomDropDownOptions = (
  mf: MultiFranchisesBasicState[]
) => {
  return mf?.map(
    (franchise: MultiFranchisesBasicState, index: number): CustoDropDownItemProps => {
      return {
        value: franchise.id,
        key: index,
        text: franchise.responsible_name
      };
    }
  );
};

export const UsersToCustomDropDownOptions = (completeValue: boolean, users: any[]) => {
  return users.map((user: any, index: number): CustoDropDownItemProps => {
    return {
      value: completeValue ? { ...user } : user.id,
      key: index,
      text: user.name
    };
  });
};

export const addMonthToDate = (date: Date | string, months: number): Date => {
  return moment(date).add(months, 'months').toDate();
};

export const subtractMonthToDate = (date: Date | string, months: number): Date => {
  return moment(date).subtract(months, 'months').toDate();
};

export const sortAssistanceOptionsByValue = (
  options: Array<CustomDropDownOptions>
): Array<CustomDropDownOptions> => {
  return options.sort((a, b) =>
    a.value.value > b.value.value ? 1 : b.value.value > a.value.value ? -1 : 0
  );
};

export const round = (value: number, decimals: number): number =>
  Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);

export const translateUserStatus = (status: string): string => {
  switch (status) {
    case UserStatusEnum.active:
      return 'ATIVO';
    case UserStatusEnum.inactive:
      return 'INATIVO';
    case UserStatusEnum.pending:
      return 'AGUARDANDO CONFIRMAÇÃO';
    default:
      return 'INDEFINIDO';
  }
};

export const translateUserRole = (role: string): string => {
  switch (role) {
    case UserRolesEnum.brokerAdmin:
      return TranslatedBrokerRolesEnum.brokerAdmin;
    case UserRolesEnum.brokerAnalyst:
      return TranslatedBrokerRolesEnum.brokerAnalyst;
    case UserRolesEnum.accountManager:
      return TranslatedBrokerRolesEnum.accountManager;
    case UserRolesEnum.claimAnalyst:
      return TranslatedBrokerRolesEnum.claimAnalyst;
    case UserRolesEnum.cancelationAnalyst:
      return TranslatedBrokerRolesEnum.cancelationAnalyst;
    case UserRolesEnum.franchiseAdmin:
      return TranslatedBrokerRolesEnum.franchiseAdmin;
    case UserRolesEnum.estateAdmin:
      return TranslatedBrokerRolesEnum.estateAdmin;
    case UserRolesEnum.estateUser:
      return TranslatedBrokerRolesEnum.estateUser;
    case UserRolesEnum.estateQuotation:
      return TranslatedBrokerRolesEnum.estateQuotation;
    case UserRolesEnum.multiFranchiseAdmin:
      return TranslatedBrokerRolesEnum.multiFranchiseAdmin;
    case UserRolesEnum.backofficeTest:
      return TranslatedBrokerRolesEnum.backofficeTest;
    default:
      return 'INDEFINIDO';
  }
};

export const setAllStepTouched = (errors: any, step: string) => {
  const touched: any = {};
  touched[step] = {};
  Object.keys(errors).map(key => {
    touched[step][key] = true;
  });
  return touched;
};

export const FormatCurrency = (n: number | undefined) => {
  const v = n || 0;
  const currency = new Intl.NumberFormat('pt-br', {
    minimumFractionDigits: 2
  }).format(v);
  return currency;
};

export const UnMaskCurrency = (value: string) => {
  const onlyDigits = value
    .split('')
    .filter((s: any) => /\d/.test(s))
    .join('')
    .padStart(2, '0');
  const digitsFloat = onlyDigits.slice(0, -2) + '.' + onlyDigits.slice(-2);
  return Number(digitsFloat);
};

export const isEmptyObject = (obj: any): boolean => {
  return Object.keys(obj).length === 0;
};

export const dateDiffInYears = (dateold: Date, datenew: Date) => {
  const ynew = datenew.getFullYear();
  const mnew = datenew.getMonth();
  const dnew = datenew.getDate();
  const yold = dateold.getFullYear();
  const mold = dateold.getMonth();
  const dold = dateold.getDate();
  let diff = ynew - yold;
  if (mold > mnew) diff--;
  else {
    if (mold == mnew) {
      if (dold > dnew) diff--;
    }
  }
  return diff;
};

export function monthDiff(dateFrom: Date, dateTo: Date) {
  return (
    dateTo.getMonth() -
    dateFrom.getMonth() +
    12 * (dateTo.getFullYear() - dateFrom.getFullYear())
  );
}

export function daysDiff(a: Date, b: Date) {
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  return Math.floor((utc2 - utc1) / MS_PER_DAY);
}

export function diffHours(a: Date, b: Date) {
  const utc1 = a.getTime();
  const utc2 = b.getTime();
  const MS_PER_HOUR = 1000 * 60 * 60;
  return (utc2 - utc1) / MS_PER_HOUR;
}

export function findObjectInArray(array: any[], key: string, value: string) {
  return array.find((obj: any) => obj[key] === value);
}

export function isOlderDate(base: Date, compare: Date) {
  const baseYear = base.getFullYear();
  const baseMonth = base.getMonth();
  const baseDay = base.getDate();
  const compareYear = compare.getFullYear();
  const compareMonth = compare.getMonth();
  const compareDay = compare.getDate();

  if (baseYear !== compareYear && compareYear < baseYear) {
    return true;
  } else {
    if (baseMonth !== compareMonth && compareMonth < baseMonth) {
      return true;
    } else {
      if (baseDay !== compareDay && compareDay < baseDay) {
        return true;
      } else {
        return false;
      }
    }
  }
}

export { isCpf as cpfValidity, isCnpj as cnpjValidity } from './shared/documents';

export function isEmail(text: string): boolean {
  if (!text) return false;

  // Regex que rejeita acentos e valida formato de email
  // Aceita: letras, números, pontos, underscores, hífens e sinais de mais
  // Caracteres especiais devem estar entre caracteres alfanuméricos
  const emailRegex = /^[a-zA-Z0-9]+([._+-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;

  return emailRegex.test(text);
}

export const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export function add365Days(date: Date) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + 365);
  return newDate;
}

export function useDebounce(fn: any, delay: number) {
  const [debounced, setDebounced] = useState(fn);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounced(fn);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [fn, delay]);
  return debounced;
}
